import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Loader2, MoreHorizontal, Sparkles } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import type { MarketingSuggestion } from '@/types/marketingProactive'
import { KIND_LABEL, KIND_TONE, kindIcon } from './proactiveMeta'
import { useBuildKit } from './useBuildKit'

/**
 * One "Next moves" card: why it showed up, and one button that drafts the
 * whole kit. "Not now" hides it for three days; the menu has the rest.
 */
export function SuggestionCard({ suggestion }: { suggestion: MarketingSuggestion }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const build = useBuildKit()
  const Icon = kindIcon(suggestion.kind, suggestion.payload)

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['marketing', 'home'] })
    queryClient.invalidateQueries({ queryKey: ['marketing', 'suggestions'] })
  }
  const snooze = useMutation({
    mutationFn: (days: number) => apiClient.snoozeMarketingSuggestion(suggestion.id, days),
    onSuccess: refresh,
    onError: () => toast({ title: 'Could not snooze', variant: 'destructive' }),
  })
  const dismiss = useMutation({
    mutationFn: () => apiClient.dismissMarketingSuggestion(suggestion.id),
    onSuccess: () => {
      refresh()
      toast({ title: 'Dismissed', description: "This idea won't come back." })
    },
    onError: () => toast({ title: 'Could not dismiss', variant: 'destructive' }),
  })

  const hasKit = Boolean(suggestion.kit_id)
  const building = build.isPending

  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 sm:flex-row sm:items-start">
      <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', KIND_TONE[suggestion.kind])}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {KIND_LABEL[suggestion.kind]}
          </span>
          {suggestion.status === 'snoozed' && <span className="text-xs text-muted-foreground">· snoozed</span>}
          {suggestion.kit_status === 'draft' && <span className="text-xs text-primary">· kit in progress</span>}
        </div>
        <p className="font-medium leading-snug">{suggestion.title}</p>
        <p className="text-sm text-muted-foreground">{suggestion.reason}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1 sm:pl-2">
        {hasKit ? (
          <Button size="sm" asChild>
            <Link to="/admin/marketing/kits" search={{ kit: suggestion.kit_id }}>
              Open kit
            </Link>
          </Button>
        ) : (
          <Button size="sm" disabled={building} onClick={() => build.mutate({ suggestion_id: suggestion.id })}>
            {building ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
            {building ? 'Drafting…' : 'Build kit'}
          </Button>
        )}
        <Button size="sm" variant="ghost" disabled={snooze.isPending} onClick={() => snooze.mutate(3)}>
          Not now
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="px-2" aria-label="More options">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => snooze.mutate(7)}>Remind me in a week</DropdownMenuItem>
            <DropdownMenuItem onClick={() => dismiss.mutate()} className="text-destructive focus:text-destructive">
              Dismiss for good
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
